import React, { useState, useCallback } from 'react'
import { Player } from '@remotion/player'
import { BRollComposition } from '../remotion/Root'
import { useBilling, costSuffix } from '../contexts/BillingContext'
import api from '../services/api'

const SCENE_LABELS = {
  ObjectShowcase: 'Objeto animado',
  MapRoute: 'Mapa con ruta',
  FlowDiagram: 'Diagrama de flujo',
  DataViz: 'Visualizacion de datos',
  TextReveal: 'Texto cinematico',
  Timeline: 'Linea de tiempo',
  ComparisonScene: 'Comparacion',
  ConceptIllustration: 'Concepto ilustrado',
  CodeBlock: 'Bloque de codigo',
  QuoteHighlight: 'Cita destacada',
  StatGrid: 'Grid de estadisticas',
  ListReveal: 'Lista animada',
  ZoomText: 'Texto dramatico',
}

const SCENE_ICONS = {
  ObjectShowcase: '📦',
  MapRoute: '🗺️',
  FlowDiagram: '🔄',
  DataViz: '📊',
  TextReveal: '✍️',
  Timeline: '📅',
  ComparisonScene: '⚖️',
  ConceptIllustration: '💡',
  CodeBlock: '💻',
  QuoteHighlight: '❝',
  StatGrid: '🎯',
  ListReveal: '📋',
  ZoomText: '⚡',
}

const TEXTURE_LABELS = {
  paper: 'papel',
  metal: 'metal',
  glass: 'cristal',
  organic: 'orgánico',
  neon: 'neón',
  minimal: 'minimal',
}

/**
 * BRollPanel — Panel inline dentro de cada SectionCard.
 *
 * Props:
 *   section              — objeto de seccion del guion
 *   scriptId             — ID del script
 *   onBRollUpdate        — callback(sectionId, recommendation)
 *   hasVisualBrief       — si el script ya tiene un Visual Brief generado
 *   onGenerateVisualBrief — callback para disparar la generación del brief
 *   briefLoading         — true mientras se genera el brief
 */
export default function BRollPanel({
  section,
  scriptId,
  onBRollUpdate,
  hasVisualBrief = false,
  onGenerateVisualBrief,
  briefLoading = false,
}) {
  const { isProPlan, showUpgradePrompt, creditWeights, hasByok } = useBilling()
  // El precio se dice antes de pulsar, aquí también.
  const suggestCost = costSuffix(creditWeights?.broll_suggest, hasByok)
  const renderCost = costSuffix(creditWeights?.broll_render, hasByok)
  const [loading, setLoading] = useState(false)
  const [showPlayer, setShowPlayer] = useState(false)
  const [renderStatus, setRenderStatus] = useState(null)
  const [renderUrl, setRenderUrl] = useState(null)
  const [pollingInterval, setPollingInterval] = useState(null)

  const broll = section.broll

  const handleSuggest = useCallback(async () => {
    if (!isProPlan) {
      showUpgradePrompt('broll', 'pro')
      return
    }
    setLoading(true)
    try {
      const res = await api.post(`/scripts/${scriptId}/broll/suggest`, {
        section_ids: [section.id],
      })
      const rec = res.data.recommendations?.[section.id]
      if (rec) {
        onBRollUpdate(section.id, 'broll', rec)
        setShowPlayer(true)
      }
    } catch {
      // El interceptor de api.js maneja 402
    } finally {
      setLoading(false)
    }
  }, [isProPlan, showUpgradePrompt, scriptId, section.id, onBRollUpdate])

  const handleToggleEnabled = useCallback(() => {
    if (!broll) return
    onBRollUpdate(section.id, 'broll', { ...broll, enabled: !broll.enabled })
  }, [broll, section.id, onBRollUpdate])

  const handleRender = useCallback(async () => {
    setRenderStatus('queued')
    try {
      const res = await api.post(`/scripts/${scriptId}/broll/render`, {
        section_id: section.id,
      })
      const { job_id } = res.data

      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/scripts/${scriptId}/broll/render/${job_id}`)
          const { status, output_url } = statusRes.data
          setRenderStatus(status)
          if (status === 'done' && output_url) {
            setRenderUrl(output_url)
            clearInterval(interval)
            setPollingInterval(null)
          } else if (status === 'error') {
            clearInterval(interval)
            setPollingInterval(null)
          }
        } catch {
          clearInterval(interval)
          setPollingInterval(null)
          setRenderStatus('error')
        }
      }, 3000)
      setPollingInterval(interval)
    } catch {
      setRenderStatus(null)
    }
  }, [scriptId, section.id])

  // ── Estado vacío: sin recomendación ──────────────────────────────
  if (!broll) {
    return (
      <div className="mt-2 pt-2 border-t border-ds-border space-y-1.5">
        {/* Brief visual — aparece si no existe, para motivar generarlo primero */}
        {!hasVisualBrief && onGenerateVisualBrief && (
          <button
            onClick={onGenerateVisualBrief}
            disabled={briefLoading || !isProPlan}
            className="flex items-center gap-1.5 text-[9px] text-[#FF9F0A]/70 hover:text-[#FF9F0A] transition-colors disabled:opacity-40"
            title="Genera la identidad visual del vídeo antes de sugerir escenas"
          >
            {briefLoading ? (
              <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <span>🎨</span>
            )}
            <span>{briefLoading ? 'Generando brief...' : 'Brief visual primero'}</span>
          </button>
        )}

        {hasVisualBrief && (
          <div className="flex items-center gap-1 text-[9px] text-[#30D158]/60">
            <span>✓</span>
            <span>Brief visual activo</span>
          </div>
        )}

        <button
          onClick={handleSuggest}
          disabled={loading}
          className="flex items-center gap-2 text-[10px] text-[#6E6E73] hover:text-[#BF5AF2] transition-colors disabled:opacity-50"
        >
          {loading ? (
            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <span>🎬</span>
          )}
          <span>{loading ? 'Dirigiendo arte...' : `Dirigir B-Roll${suggestCost}`}</span>
          {!isProPlan && (
            <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold bg-[#BF5AF2]/20 text-[#BF5AF2] uppercase tracking-wider">
              PRO
            </span>
          )}
        </button>
      </div>
    )
  }

  // ── Con recomendación ─────────────────────────────────────────────
  const durationFrames = Math.round((broll.duration_seconds || 5) * 30)
  const sceneLabel = SCENE_LABELS[broll.scene_type] || broll.scene_type
  const sceneIcon = SCENE_ICONS[broll.scene_type] || '🎬'
  const texture = broll.art_direction?.texture
  const textureLabel = texture ? TEXTURE_LABELS[texture] : null

  return (
    <div className="mt-2 pt-2 border-t border-ds-border space-y-2">
      {/* Header: tipo de escena + controles */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px]">{sceneIcon}</span>
        <span className="text-[10px] font-semibold text-[#BF5AF2]">{sceneLabel}</span>

        {/* Textura de arte */}
        {textureLabel && (
          <span className="text-[8px] px-1.5 py-0.5 border border-[#FF9F0A]/25 text-[#FF9F0A]/70 uppercase tracking-wider">
            {textureLabel}
          </span>
        )}

        <span className="flex-1 text-[10px] text-[#6E6E73] truncate">{broll.description}</span>

        {/* Toggle activo/inactivo */}
        <button
          onClick={handleToggleEnabled}
          className={`text-[9px] px-2 py-0.5 border transition-all ${
            broll.enabled
              ? 'border-[#30D158]/40 text-[#30D158] bg-[#30D158]/10'
              : 'border-ds-border text-[#4A4A4A]'
          }`}
        >
          {broll.enabled ? 'Activo' : 'Inactivo'}
        </button>

        {/* Ver preview */}
        <button
          onClick={() => setShowPlayer(p => !p)}
          className="text-[10px] text-[#007AFF] hover:text-white transition-colors"
        >
          {showPlayer ? 'Ocultar' : 'Ver'}
        </button>

        {/* Regenerar */}
        <button
          onClick={handleSuggest}
          disabled={loading}
          className="text-[10px] text-[#6E6E73] hover:text-white transition-colors disabled:opacity-50"
          title={`Regenerar${suggestCost}`}
        >
          {loading ? (
            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            '↺'
          )}
        </button>
      </div>

      {/* Metáfora — el insight clave del director */}
      {broll.metaphor && (
        <p className="text-[9px] text-[#BF5AF2]/60 italic leading-relaxed">↳ {broll.metaphor}</p>
      )}

      {/* Razonamiento IA */}
      <p className="text-[10px] text-[#4A4A4A] italic leading-relaxed">{broll.reasoning}</p>

      {/* Art direction note (si existe) */}
      {broll.art_direction?.mood_note && (
        <p className="text-[9px] text-[#6E6E73]/70 leading-relaxed border-l-2 border-[#6E6E73]/20 pl-2">
          {broll.art_direction.mood_note}
        </p>
      )}

      {/* Player preview (colapsable) */}
      {showPlayer && broll.enabled && (
        <div className="overflow-hidden border border-ds-border">
          <Player
            component={BRollComposition}
            inputProps={{
              scene_type: broll.scene_type,
              params: broll.params,
              art_direction: broll.art_direction || undefined,
            }}
            durationInFrames={durationFrames}
            compositionWidth={1920}
            compositionHeight={1080}
            fps={30}
            style={{ width: '100%', aspectRatio: '16/9' }}
            controls
            loop
          />
        </div>
      )}

      {/* Export a MP4 */}
      {broll.enabled && (
        <div className="flex items-center gap-3">
          {renderStatus === null && (
            <button
              onClick={handleRender}
              className="flex items-center gap-1.5 text-[10px] text-[#6E6E73] hover:text-white border border-ds-border hover:border-ds-border-mid px-2.5 py-1 transition-all"
            >
              ⬇ Exportar MP4{renderCost}
            </button>
          )}
          {(renderStatus === 'queued' || renderStatus === 'rendering') && (
            <span className="flex items-center gap-1.5 text-[10px] text-[#FF9F0A]">
              <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
              Renderizando...
            </span>
          )}
          {renderStatus === 'done' && renderUrl && (
            <button
              onClick={async () => {
                try {
                  const res = await api.get(renderUrl, { responseType: 'blob' })
                  const url = URL.createObjectURL(res.data)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `broll-${section.id.slice(0, 8)}.mp4`
                  document.body.appendChild(a)
                  a.click()
                  a.remove()
                  URL.revokeObjectURL(url)
                } catch {
                  // Silently fail — button stays visible for retry
                }
              }}
              className="flex items-center gap-1.5 text-[10px] text-[#30D158] border border-[#30D158]/30 px-2.5 py-1 hover:bg-[#30D158]/10 transition-all cursor-pointer"
            >
              ✓ Descargar MP4
            </button>
          )}
          {renderStatus === 'error' && (
            <span className="text-[10px] text-ds-red">Error al renderizar</span>
          )}
        </div>
      )}
    </div>
  )
}
